/**
 * 관리자 공통 색상 선택기 필드 (단색 + 2색 그라데이션).
 *
 * - 단색 탭: Pickr (nano 테마) — `#RRGGBB`
 * - 그라데이션 탭: 형태(linear/radial) + 각도 + 시작/끝 색 2개 → CSS `linear-gradient(...)`/`radial-gradient(...)`
 *
 * 값은 항상 "CSS 문자열" 하나로 직렬화된다(서버는 `css_background_value()` 화이트리스트로 한 번 더 거른다).
 *
 * 마운트 규약:
 *   <div data-color-picker
 *        data-color-initial="#1a3365"
 *        data-color-mode="form|autosave"        (생략 시 ctx 가 있으면 autosave, 없으면 form)
 *        data-color-var="--brand-bg"             (대상 CSS 변수, 생략 시 --brand-bg)
 *        data-color-target="#sel"                (선택자, 생략 시 가장 가까운 [data-cms-block] 안의 [data-cms-color-target])
 *   ></div>
 *   같은 wrapper 안에: <input type="hidden" data-color-input data-field-key="..." data-field-block-id="...">
 *                      <code data-color-readout></code>          (선택)
 *
 * 호출:
 *   initColorPickerFields(scope)                                       // form 모드 전용
 *   initColorPickerFields(scope, { workId, saveUrl, csrfToken })       // autosave 모드 포함
 */
import { PickrWrapper, type PickrInstance, type PickrHSVaColor } from "../../components/PickrWrapper";

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const DEFAULT_FALLBACK = "#1a3365";

const SWATCHES = [
    "#1a3365",
    "#0c4a6e",
    "#1d4ed8",
    "#0f172a",
    "#000000",
    "#1a1a1a",
    "#ffffff",
    "#f5f5f4",
    "#78716c",
    "#15803d",
    "#a16207",
    "#9f1239",
    "#4c1d95",
];

const PICKR_I18N = {
    "ui:dialog": "색상 선택",
    "btn:toggle": "색상 선택 열기",
    "btn:swatch": "자주 쓰는 색",
    "btn:last-color": "이전 색",
    "aria:input": "색상 코드 입력",
    "aria:palette": "명도·채도",
    "aria:hue": "색상( hue )",
};

interface AutosaveCtx {
    workId: number;
    saveUrl: string;
    csrfToken: string;
}

type GradKind = "linear" | "radial";
type FieldMode = "solid" | "gradient";

interface GradState {
    kind: GradKind;
    angle: number;
    stops: [{ color: string; pos: number }, { color: string; pos: number }];
}

/* ------------------------------------------------------------------ helpers */

function clampHex(raw: string, fallback: string): string {
    const t = (raw ?? "").trim();
    if (!HEX.test(t)) return fallback;
    if (t.length === 4) {
        return ("#" + t[1] + t[1] + t[2] + t[2] + t[3] + t[3]).toLowerCase();
    }
    return t.toLowerCase();
}

function pickrHex(c: PickrHSVaColor): string {
    const full = c.toHEXA().toString();
    return (full.length > 7 ? full.slice(0, 7) : full).toLowerCase();
}

function clampInt(n: number, min: number, max: number, fallback: number): number {
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, Math.round(n)));
}

function parseGradient(raw: string): GradState | null {
    const v = (raw ?? "").trim();

    let kind: GradKind;
    let head = "";
    let body: string;
    let m = /^(?:repeating-)?linear-gradient\(\s*(.+)\)$/i.exec(v);
    if (m) {
        kind = "linear";
        const inner = m[1];
        const ci = inner.indexOf(",");
        if (ci > 0 && !inner.slice(0, ci).includes("#")) {
            head = inner.slice(0, ci);
            body = inner.slice(ci + 1);
        } else {
            body = inner;
        }
    } else {
        m = /^(?:repeating-)?radial-gradient\(\s*(.+)\)$/i.exec(v);
        if (!m) return null;
        kind = "radial";
        body = m[1]; // "circle, #aaa 0%, #bbb 100%" 등 — 색이 없는 앞부분 토큰은 아래 루프에서 무시됨
    }

    let angle = 135;
    if (kind === "linear" && head) {
        const am = /(-?\d+(?:\.\d+)?)deg/i.exec(head);
        if (am) angle = clampInt(parseFloat(am[1]), 0, 360, 135);
        else if (/to\s+top\b/i.test(head)) angle = 0;
        else if (/to\s+right\b/i.test(head)) angle = 90;
        else if (/to\s+bottom\b/i.test(head)) angle = 180;
        else if (/to\s+left\b/i.test(head)) angle = 270;
    }

    const stops: Array<{ color: string; pos: number | null }> = [];
    for (const part of body.split(",")) {
        const cm = /#[0-9a-fA-F]{3,6}/.exec(part);
        if (!cm) continue;
        const color = clampHex(cm[0], "");
        if (color === "") continue;
        const pm = /(\d+(?:\.\d+)?)\s*%/.exec(part);
        stops.push({ color, pos: pm ? clampInt(parseFloat(pm[1]), 0, 100, 0) : null });
    }
    if (stops.length < 2) return null;

    const first = stops[0];
    const last = stops[stops.length - 1];
    return {
        kind,
        angle,
        stops: [
            { color: first.color, pos: first.pos ?? 0 },
            { color: last.color, pos: last.pos ?? 100 },
        ],
    };
}

function serializeGradient(g: GradState): string {
    const s0 = `${g.stops[0].color} ${g.stops[0].pos}%`;
    const s1 = `${g.stops[1].color} ${g.stops[1].pos}%`;
    return g.kind === "radial"
        ? `radial-gradient(circle, ${s0}, ${s1})`
        : `linear-gradient(${g.angle}deg, ${s0}, ${s1})`;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, text?: string): HTMLElementTagNameMap[K] {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text !== undefined) n.textContent = text;
    return n;
}

function makePickr(container: HTMLElement, initial: string): PickrInstance {
    // Pickr 는 `el` 요소를 자신의 `.pickr` 루트로 교체한다(replaceChild). 컨테이너 class 를 살리려고
    // 안쪽에 빈 div 를 만들어 그걸 넘긴다 → `.admin-cpf__pickr > .pickr`, `.admin-cpf__stop-pickr > .pickr` 유지.
    const inner = document.createElement("div");
    container.appendChild(inner);
    return PickrWrapper.create({
        el: inner,
        theme: "nano",
        default: initial,
        lockOpacity: true,
        position: "bottom-start",
        i18n: PICKR_I18N,
        swatches: SWATCHES,
        components: {
            preview: true,
            opacity: false,
            hue: true,
            interaction: {
                hex: true,
                rgba: false,
                hsla: false,
                hsva: false,
                input: true,
                cancel: false,
                clear: false,
                save: false,
            },
        },
    });
}

async function autosave(ctx: AutosaveCtx, blockId: number, fieldKey: string, value: string): Promise<void> {
    if (!ctx.saveUrl || ctx.workId <= 0 || blockId <= 0 || fieldKey === "") return;
    const fd = new FormData();
    fd.append("_token", ctx.csrfToken);
    fd.append("work_id", String(ctx.workId));
    fd.append("block_id", String(blockId));
    fd.append("field_key", fieldKey);
    fd.append("value_type", "text");
    fd.append("value_text", value);
    try {
        await fetch(ctx.saveUrl, {
            method: "POST",
            body: fd,
            headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        });
    } catch {
        /* silent — autosave UX */
    }
}

/* ------------------------------------------------------------------ field */

function buildField(mount: HTMLElement, ctx?: AutosaveCtx): void {
    if (mount.dataset.colorPickerReady === "1") return;
    mount.dataset.colorPickerReady = "1";

    const wrapper = mount.closest<HTMLElement>("[data-cms-block-color]") ?? mount.parentElement ?? mount;
    const hidden =
        wrapper.querySelector<HTMLInputElement>("[data-color-input]") ??
        wrapper.querySelector<HTMLInputElement>("[data-cms-color-input]") ??
        wrapper.querySelector<HTMLInputElement>('input[type="hidden"]');
    const readout =
        wrapper.querySelector<HTMLElement>("[data-color-readout]") ??
        wrapper.querySelector<HTMLElement>("[data-cms-color-readout]");

    const mode = (mount.dataset.colorMode ?? (ctx ? "autosave" : "form")).trim();
    const isAutosave = mode === "autosave" && !!ctx;

    // data-color-solid-only="1" → 그라데이션 탭 숨김(예: 텍스트 색상). 값은 항상 hex.
    const solidOnly = (mount.dataset.colorSolidOnly ?? "").trim() === "1";

    const varName = (mount.dataset.colorVar ?? "--brand-bg").trim() || "--brand-bg";
    const targetSel = (mount.dataset.colorTarget ?? "").trim();
    const block = mount.closest<HTMLElement>("[data-cms-block]");
    const target: HTMLElement | null = targetSel
        ? document.querySelector<HTMLElement>(targetSel)
        : block?.querySelector<HTMLElement>("[data-cms-color-target]") ?? null;

    const blockId = Number(hidden?.dataset.fieldBlockId ?? mount.dataset.fieldBlockId ?? "0") || 0;
    const fieldKey = (hidden?.dataset.fieldKey ?? "").trim();

    const rawInitial = (mount.dataset.colorInitial ?? hidden?.value ?? "").trim();
    const fallback = HEX.test(rawInitial) ? clampHex(rawInitial, DEFAULT_FALLBACK) : DEFAULT_FALLBACK;
    const parsedGrad = solidOnly ? null : parseGradient(rawInitial);
    const initialSolid = parsedGrad ? fallback : clampHex(rawInitial, fallback);
    let currentMode: FieldMode = parsedGrad ? "gradient" : "solid";

    /* ---- DOM ---- */
    const root = el("div", "admin-cpf");

    const tabs = el("div", "admin-cpf__modes");
    const tabSolid = el("button", "admin-cpf__mode", "단색");
    tabSolid.type = "button";
    const tabGrad = el("button", "admin-cpf__mode", "그라데이션");
    tabGrad.type = "button";
    tabs.append(tabSolid, tabGrad);
    if (solidOnly) tabs.hidden = true;

    const paneSolid = el("div", "admin-cpf__pane admin-cpf__pane--solid");
    const solidPickrMount = el("div", "admin-cpf__pickr");
    paneSolid.append(solidPickrMount);

    const paneGrad = el("div", "admin-cpf__pane admin-cpf__pane--gradient");
    const gradControls = el("div", "admin-cpf__grad-controls");
    const typeWrap = el("label", "admin-cpf__grad-field");
    typeWrap.append(el("span", "admin-cpf__grad-label", "형태"));
    const typeSel = el("select", "admin-cpf__grad-type");
    typeSel.append(new Option("선형", "linear"), new Option("원형", "radial"));
    typeWrap.append(typeSel);
    const angleWrap = el("label", "admin-cpf__grad-field admin-cpf__grad-field--angle");
    angleWrap.append(el("span", "admin-cpf__grad-label", "각도"));
    const angleInput = el("input", "admin-cpf__grad-angle");
    angleInput.type = "number";
    angleInput.min = "0";
    angleInput.max = "360";
    angleInput.step = "5";
    angleWrap.append(angleInput, el("span", "admin-cpf__grad-unit", "°"));
    gradControls.append(typeWrap, angleWrap);

    const stopsWrap = el("div", "admin-cpf__stops");
    const stop0Mount = el("div", "admin-cpf__stop-pickr");
    const stop1Mount = el("div", "admin-cpf__stop-pickr");
    const pos0 = el("input", "admin-cpf__stop-pos");
    const pos1 = el("input", "admin-cpf__stop-pos");
    for (const inp of [pos0, pos1]) {
        inp.type = "number";
        inp.min = "0";
        inp.max = "100";
        inp.step = "1";
    }
    const stop0 = el("div", "admin-cpf__stop");
    stop0.append(el("span", "admin-cpf__stop-tag", "시작"), stop0Mount, pos0, el("span", "admin-cpf__grad-unit", "%"));
    const stop1 = el("div", "admin-cpf__stop");
    stop1.append(el("span", "admin-cpf__stop-tag", "끝"), stop1Mount, pos1, el("span", "admin-cpf__grad-unit", "%"));
    stopsWrap.append(stop0, stop1);

    const gradPreview = el("div", "admin-cpf__grad-preview");
    paneGrad.append(gradControls, stopsWrap, gradPreview);

    root.append(tabs, paneSolid, paneGrad);
    mount.append(root);

    /* ---- Pickr instances ---- */
    const initStops: GradState["stops"] = parsedGrad
        ? parsedGrad.stops
        : [
              { color: initialSolid, pos: 0 },
              { color: "#ffffff", pos: 100 },
          ];
    const solidPickr = makePickr(solidPickrMount, initialSolid);
    const stopPickr0 = makePickr(stop0Mount, initStops[0].color);
    const stopPickr1 = makePickr(stop1Mount, initStops[1].color);
    typeSel.value = parsedGrad ? parsedGrad.kind : "linear";
    angleInput.value = String(parsedGrad ? parsedGrad.angle : 135);
    pos0.value = String(initStops[0].pos);
    pos1.value = String(initStops[1].pos);

    /* ---- state ---- */
    let solidHex = initialSolid;
    let stopHex0 = initStops[0].color;
    let stopHex1 = initStops[1].color;
    let lastValue = "";
    let saveTimer: number | null = null;

    const readGrad = (): GradState => ({
        kind: typeSel.value === "radial" ? "radial" : "linear",
        angle: clampInt(parseFloat(angleInput.value), 0, 360, 135),
        stops: [
            { color: stopHex0, pos: clampInt(parseFloat(pos0.value), 0, 100, 0) },
            { color: stopHex1, pos: clampInt(parseFloat(pos1.value), 0, 100, 100) },
        ],
    });

    const refreshGradPreview = (): void => {
        gradPreview.style.background = serializeGradient(readGrad());
    };

    const syncTabs = (): void => {
        tabSolid.classList.toggle("is-active", currentMode === "solid");
        tabGrad.classList.toggle("is-active", currentMode === "gradient");
        paneSolid.hidden = currentMode !== "solid";
        paneGrad.hidden = currentMode !== "gradient";
        angleWrap.style.display = typeSel.value === "radial" ? "none" : "";
    };

    const emit = (push: boolean): void => {
        const value = currentMode === "gradient" ? serializeGradient(readGrad()) : solidHex;
        if (hidden) hidden.value = value;
        if (readout) readout.textContent = value;
        if (target) target.style.setProperty(varName, value);
        if (!push || value === lastValue) {
            lastValue = value;
            return;
        }
        lastValue = value;
        if (isAutosave && ctx) {
            if (saveTimer != null) window.clearTimeout(saveTimer);
            saveTimer = window.setTimeout(() => {
                saveTimer = null;
                void autosave(ctx, blockId, fieldKey, value);
            }, 350);
        }
    };

    /* ---- wire ---- */
    const onSolid = (c: PickrHSVaColor): void => {
        solidHex = pickrHex(c);
        solidPickr.applyColor(true);
        if (currentMode === "solid") emit(true);
    };
    solidPickr.on("change", onSolid);
    solidPickr.on("swatchselect", onSolid);
    solidPickr.on("init", () => solidPickr.applyColor(true));

    const onStop = (which: 0 | 1, c: PickrHSVaColor): void => {
        const hex = pickrHex(c);
        if (which === 0) {
            stopHex0 = hex;
            stopPickr0.applyColor(true);
        } else {
            stopHex1 = hex;
            stopPickr1.applyColor(true);
        }
        refreshGradPreview();
        if (currentMode === "gradient") emit(true);
    };
    stopPickr0.on("change", (c: PickrHSVaColor) => onStop(0, c));
    stopPickr0.on("swatchselect", (c: PickrHSVaColor) => onStop(0, c));
    stopPickr0.on("init", () => stopPickr0.applyColor(true));
    stopPickr1.on("change", (c: PickrHSVaColor) => onStop(1, c));
    stopPickr1.on("swatchselect", (c: PickrHSVaColor) => onStop(1, c));
    stopPickr1.on("init", () => stopPickr1.applyColor(true));

    const onGradControl = (): void => {
        syncTabs();
        refreshGradPreview();
        if (currentMode === "gradient") emit(true);
    };
    typeSel.addEventListener("change", onGradControl);
    angleInput.addEventListener("input", onGradControl);
    pos0.addEventListener("input", onGradControl);
    pos1.addEventListener("input", onGradControl);

    tabSolid.addEventListener("click", () => {
        if (currentMode === "solid") return;
        currentMode = "solid";
        syncTabs();
        emit(true);
    });
    tabGrad.addEventListener("click", () => {
        if (currentMode === "gradient") return;
        currentMode = "gradient";
        syncTabs();
        refreshGradPreview();
        emit(true);
    });

    syncTabs();
    refreshGradPreview();
    emit(false);
}

/* ------------------------------------------------------------------ export */

export function initColorPickerFields(scope: HTMLElement | null | undefined, ctx?: AutosaveCtx): void {
    if (!scope) return;
    scope.querySelectorAll<HTMLElement>("[data-color-picker]").forEach((mount) => buildField(mount, ctx));
}
